import { useMemo, useState } from "react";
import type { Reservation } from "@/types/reservation";
import { dateKey, todayTokyoDateInput } from "@/lib/date";
import { groupByTokyoDate } from "./calendarUtils";
import { ReservationCard } from "@/components/ReservationCard";
import { EmptyState } from "@/components/EmptyState";

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(Date.UTC(year, month, 1));
  const startOffset = first.getUTCDay();
  const gridStart = new Date(first);
  gridStart.setUTCDate(first.getUTCDate() - startOffset);

  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setUTCDate(gridStart.getUTCDate() + i);
    days.push(d);
  }
  return days;
}

export function MobileAgenda({
  reservations,
  pcNameFor,
  userNameFor,
  onSelectReservation,
}: {
  reservations: Reservation[];
  pcNameFor: (pcId: number) => string;
  userNameFor: (userId: number) => string;
  onSelectReservation: (reservation: Reservation) => void;
}) {
  const todayKey = todayTokyoDateInput();
  const [cursor, setCursor] = useState(() => {
    const [y, m] = todayKey.split("-").map(Number);
    return { year: y, month: m - 1 };
  });
  const [selectedDate, setSelectedDate] = useState(todayKey);

  const grouped = useMemo(() => groupByTokyoDate(reservations), [reservations]);
  const days = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  const selectedReservations = grouped.get(selectedDate) ?? [];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button
          className="btn btn-secondary"
          style={{ height: 36, padding: "0 12px" }}
          onClick={() => setCursor((c) => normalizeMonth(c.year, c.month - 1))}
        >
          ＜
        </button>
        <strong>
          {cursor.year}年{cursor.month + 1}月
        </strong>
        <button
          className="btn btn-secondary"
          style={{ height: 36, padding: "0 12px" }}
          onClick={() => setCursor((c) => normalizeMonth(c.year, c.month + 1))}
        >
          ＞
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 8 }}>
        {WEEKDAYS.map((w) => (
          <div key={w} style={{ textAlign: "center", fontSize: 11, color: "var(--color-text-muted)" }}>
            {w}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 20 }}>
        {days.map((d) => {
          const key = dateKey(d.toISOString());
          const inMonth = d.getUTCMonth() === cursor.month;
          const count = grouped.get(key)?.length ?? 0;
          const isSelected = key === selectedDate;
          const isToday = key === todayKey;
          return (
            <button
              key={key}
              onClick={() => setSelectedDate(key)}
              style={{
                aspectRatio: "1",
                border: isSelected ? "2px solid var(--color-primary)" : "1px solid var(--color-border)",
                borderRadius: 10,
                background: isSelected ? "var(--color-primary-soft)" : "var(--color-surface)",
                color: inMonth ? "var(--color-text)" : "var(--color-text-muted)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                fontSize: 12,
                fontWeight: isToday ? 800 : 500,
                padding: 0,
              }}
            >
              <span>{d.getUTCDate()}</span>
              {count > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    color: "var(--color-primary)",
                    fontWeight: 700,
                  }}
                >
                  {count}件
                </span>
              )}
            </button>
          );
        })}
      </div>

      <h3 className="section-title">{selectedDate} の予定</h3>
      {selectedReservations.length === 0 ? (
        <EmptyState message="この日の予定はありません。" />
      ) : (
        selectedReservations.map((r) => (
          <ReservationCard
            key={r.id}
            reservation={r}
            pcName={pcNameFor(r.pc_id)}
            userName={userNameFor(r.user_id)}
            onClick={() => onSelectReservation(r)}
          />
        ))
      )}
    </div>
  );
}

function normalizeMonth(year: number, month: number): { year: number; month: number } {
  if (month < 0) return { year: year - 1, month: 11 };
  if (month > 11) return { year: year + 1, month: 0 };
  return { year, month };
}
